import { createContext, useContext, useState, useEffect } from "react";
import { useAppSelector } from "app/store/hooks";
import { selectUser } from "src/app/auth/user/store/userSlice";
import { useCleanSession } from "src/api/cleaningSessions";

const CleaningSessionContext = createContext(null);

export const CleaningSessionProvider = ({ children }) => {
  const user = useAppSelector(selectUser);
  const cleanerId = user.uid;
  const {
    startCleaningSession,
    completeCleaningSession,
    fetchActiveCleaningSessions,
    fetchAllCleaningSessions,
  } = useCleanSession();
  const [activeSession, setActiveSession] = useState(null);

  const isCleaner = () => {
    if (Array.isArray(user.role)) return user.role.includes("Cleaner");
    return user.role === "Cleaner";
  };

  // Fetch active session once or periodically
  useEffect(() => {
    if (!isCleaner()) return;

    const fetchActive = async () => {
      try {
        const res = await fetchActiveCleaningSessions();
        const mySession = res.data.find(
          (session) => session.cleaner_id === user.uid
        );
        // Only update if the session is different to avoid unnecessary re-renders
        setActiveSession((prevSession) => {
          const newSession = mySession || null;
          if (prevSession?.id !== newSession?.id) {
            return newSession;
          }
          return prevSession;
        });
      } catch (err) {
        console.error("Failed to fetch active session", err);
      }
    };

    fetchActive();
    const interval = setInterval(fetchActive, 10000); // every 10 sec
    return () => clearInterval(interval);
  }, [user.uid]);

  return (
    <CleaningSessionContext.Provider
      value={{
        activeSession,
        setActiveSession,
        startCleaningSession,
        completeCleaningSession,
        fetchActiveCleaningSessions,
        fetchAllCleaningSessions,
        cleanerId,
        isCleaner,
      }}
    >
      {children}
    </CleaningSessionContext.Provider>
  );
};

export const useCleaningSession = () => useContext(CleaningSessionContext);
